import { getTimeZoneDetails, getUserTimeZone } from "./timezone";

// Calculate duration between two UTC timestamps
export const calculateDurationFromUTC = (
  startUTC?: string,
  endUTC?: string,
): string => {
  if (!startUTC || !endUTC) return "—";

  const start = new Date(startUTC);
  const end = new Date(endUTC);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) return "—";

  const diffMs = end.getTime() - start.getTime();
  if (diffMs <= 0) return "—";

  const totalMinutes = Math.floor(diffMs / 60000);

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) return `${minutes} min`;
  if (minutes === 0) return `${hours} hr`;

  return `${hours} hr ${minutes} min`;
};

// Convert UTC → local time string (01:30 AM)
export const formatUTCToLocalTime = (
  utcTime?: string,
  options?: {
    hour12?: boolean;
    withSeconds?: boolean;
  },
): string => {
  if (!utcTime) return "—";

  const date = new Date(utcTime);
  if (isNaN(date.getTime())) return "—";

  const { hour12 = true, withSeconds = false } = options || {};

  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    second: withSeconds ? "2-digit" : undefined,
    hour12,
  });
};

/**
 * Convert UTC → display time string (e.g. "07:30 PM") in a specific IANA timezone.
 * Use this instead of formatUTCToLocalTime when you need the candidate's timezone,
 * not the HR browser's local timezone.
 */
export const formatUTCToTzTime = (
  utcTime?: string,
  timeZone?: string,
  options?: { hour12?: boolean; withSeconds?: boolean },
): string => {
  if (!utcTime) return "—";
  const date = new Date(utcTime);
  if (isNaN(date.getTime())) return "—";
  if (!timeZone) return formatUTCToLocalTime(utcTime, options);
  const { hour12 = true, withSeconds = false } = options || {};
  try {
    return date.toLocaleTimeString(undefined, {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      second: withSeconds ? "2-digit" : undefined,
      hour12,
    });
  } catch {
    return formatUTCToLocalTime(utcTime, options);
  }
};

//Convert UTC → local date string
export const formatUTCToLocalDate = (utc?: string): string => {
  if (!utc) return "—";

  const date = new Date(utc);
  if (isNaN(date.getTime())) return "—";

  return date.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Combine local date + time → UTC ISO string (for API)
export const combineDateAndTimeToUTC = (
  date: Date,
  time: string,
  timeZone?: string,
): string => {
  if (!date || !time) return "";

  const [hours, minutes] = time.split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) return "";

  if (timeZone) {
    const tzDetails = getTimeZoneDetails(timeZone);
    const offsetMinutes = tzDetails.offsetMinutes;
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const utcMs =
      Date.UTC(year, month, day, hours, minutes, 0, 0) -
      offsetMinutes * 60 * 1000;
    return new Date(utcMs).toISOString();
  }

  const localDateTime = new Date(date);
  localDateTime.setHours(hours, minutes, 0, 0);

  return localDateTime.toISOString();
};

/**
 * Parses any time string ("HH:mm", "hh:mm a", Date, or ISO string) into { hours, minutes }
 */
export const parseTimeToHoursMinutes = (
  timeVal?: string | Date | null,
): { hours: number; minutes: number } | null => {
  if (!timeVal) return null;

  if (timeVal instanceof Date) {
    if (isNaN(timeVal.getTime())) return null;
    return { hours: timeVal.getHours(), minutes: timeVal.getMinutes() };
  }

  if (typeof timeVal !== "string") return null;

  const trimmed = timeVal.trim();
  if (!trimmed) return null;

  const match = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([aApP][mM])?$/);

  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const meridiem = match[3]?.toUpperCase();

    if (meridiem === "PM" && hours < 12) hours += 12;
    if (meridiem === "AM" && hours === 12) hours = 0;

    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      return { hours, minutes };
    }
    return null;
  }

  const date = new Date(trimmed);
  if (!isNaN(date.getTime())) {
    return { hours: date.getHours(), minutes: date.getMinutes() };
  }

  return null;
};

// Convert UTC/Time string → HH:mm (24-hour format) in LOCAL browser timezone
export const toLocalTimeInput = (utc?: string): string => {
  const parsed = parseTimeToHoursMinutes(utc);
  if (!parsed) return "";
  return `${String(parsed.hours).padStart(2, "0")}:${String(parsed.minutes).padStart(2, "0")}`;
};

/**
 * Converts a UTC ISO string to "HH:mm" (24-hour) in the specified IANA timezone.
 * Use this for reschedule prefill so times display in the candidate's timezone,
 * not the HR's browser timezone.
 */
export const utcToTimeInZone = (utc?: string, timeZone?: string): string => {
  if (!utc) return "";
  const date = new Date(utc);
  if (isNaN(date.getTime())) {
    // If it's already a time string (e.g. "HH:mm" or "hh:mm a"), parse directly
    return toLocalTimeInput(utc);
  }
  if (!timeZone) return toLocalTimeInput(utc);
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(date);
    const h = parts.find((p) => p.type === "hour")?.value ?? "00";
    const m = parts.find((p) => p.type === "minute")?.value ?? "00";
    // Normalize "24:xx" (midnight edge case some browsers emit) → "00:xx"
    return `${h === "24" ? "00" : h}:${m}`;
  } catch {
    return toLocalTimeInput(utc);
  }
};

/**
 * Converts a UTC ISO string to a Date object at local midnight of the date
 * that the UTC instant falls on in the specified IANA timezone.
 * Use this for reschedule prefill so the date shown matches the candidate's timezone.
 */
export const utcToDateInZone = (utc?: string, timeZone?: string): Date | undefined => {
  if (!utc) return undefined;
  const date = new Date(utc);
  if (isNaN(date.getTime())) return undefined;
  try {
    const tz = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const [year, month, day] = new Intl.DateTimeFormat("en-CA", { timeZone: tz })
      .format(date)
      .split("-")
      .map(Number);
    return new Date(year, (month ?? 1) - 1, day, 0, 0, 0, 0);
  } catch {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }
};

export interface RescheduleInterviewSource {
  interviewId?: string;
  scheduledDate?: string;
  time?: {
    startTime?: string;
    endTime?: string;
  };
  candidateTz?: string;
  notes?: string[];
}

/**
 * Extracts form initial values (date, startTime, endTime in candidateTz) from interview record.
 */
export const getRescheduleInitialValues = (
  interviewData?: RescheduleInterviewSource | null,
) => {
  if (!interviewData) return undefined;
  const tz = interviewData.candidateTz || getUserTimeZone();
  return {
    interviewId: interviewData.interviewId,
    date: utcToDateInZone(interviewData.scheduledDate, tz),
    startTime: utcToTimeInZone(interviewData.time?.startTime, tz),
    endTime: utcToTimeInZone(interviewData.time?.endTime, tz),
    candidateTz: tz,
    notes: interviewData.notes?.join("\n") ?? "",
  };
};

// Format full interview slot (UTC → Local or UTC → Candidate Timezone)
export const formatInterviewSlot = (
  interview?: RescheduleInterviewSource | null,
  options?: {
    timeZone?: string;
    includeOffset?: boolean;
  },
): string => {
  if (!interview?.scheduledDate) return "—";

  const { timeZone, includeOffset = false } = options || {};

  let dateStr = "";
  let startStr = "—";
  let endStr = "—";

  if (timeZone) {
    const dateObj = utcToDateInZone(interview.scheduledDate, timeZone);
    dateStr = dateObj ? dateObj.toLocaleDateString() : "";
    startStr = interview.time?.startTime
      ? formatUTCToTzTime(interview.time.startTime, timeZone)
      : "—";
    endStr = interview.time?.endTime
      ? formatUTCToTzTime(interview.time.endTime, timeZone)
      : "—";
  } else {
    dateStr = formatUTCToLocalDate(interview.scheduledDate);
    startStr = interview.time?.startTime
      ? formatUTCToLocalTime(interview.time.startTime)
      : "—";
    endStr = interview.time?.endTime
      ? formatUTCToLocalTime(interview.time.endTime)
      : "—";
  }

  let result = `${dateStr} | ${startStr} - ${endStr}`;
  if (includeOffset && timeZone) {
    result = `${result} | (${getTimeZoneDetails(timeZone).offset})`;
  }
  return result;
};

export const formatShortDate = (date: Date) =>
  new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "2-digit",
  }).format(date);

export function formatDate(date?: Date) {
  if (!date) return undefined;
  return date.toISOString().split("T")[0];
}

export function getFutureTime(offsetMinutes: number, baseDate = new Date()) {
  return addMinutesToTime(baseDate, offsetMinutes);
}

/**
 * Adds a given number of minutes to a time string or Date object and returns "HH:mm" (24-hour format).
 */
export function addMinutesToTime(
  time: string | Date | null | undefined,
  minutesToAdd: number,
): string {
  const parsed = parseTimeToHoursMinutes(time);
  if (!parsed) return "";

  const totalMinutes =
    (parsed.hours * 60 + parsed.minutes + minutesToAdd) % (24 * 60);
  const normalizedMinutes =
    totalMinutes < 0 ? totalMinutes + 24 * 60 : totalMinutes;

  const newHours = String(Math.floor(normalizedMinutes / 60)).padStart(2, "0");
  const newMinutes = String(normalizedMinutes % 60).padStart(2, "0");

  return `${newHours}:${newMinutes}`;
}
